import { useEffect, useRef, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

const DISMISS_KEY = "fbx_drawer_dismissed_v1"; // session-scoped
const SIGNED_KEY = "fbx_drawer_signed_v1"; // persistent

/**
 * Slim premium "access bar" — sits flush against the bottom edge.
 * Not a marketing popup. Dismissible. Persists state so it never nags.
 */
export default function StickyDropDrawer() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });
  const triggered = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    if (localStorage.getItem(SIGNED_KEY)) return;

    function show() {
      if (triggered.current) return;
      triggered.current = true;
      setVisible(true);
    }
    const onScroll = () => {
      if (window.scrollY > 700) show();
    };
    const timer = setTimeout(show, 11000);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  function dismiss() {
    setVisible(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/drop-list", {
        email,
        source: "sticky_drop_drawer",
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setState({
        status: "success",
        message: data?.message || "UPLINK LOGGED. YOU'RE ON THE LIST FOR DROP 001.",
      });
      try {
        localStorage.setItem(SIGNED_KEY, "1");
      } catch {}
      // Keep the success message visible for 3.5s so the user sees the confirmation.
      setTimeout(() => setVisible(false), 3500);
    } catch (err) {
      setState({
        status: "error",
        message: formatApiError(err.response?.data?.detail) || "TRANSMISSION FAILED",
      });
    }
  }

  if (!visible) return null;

  return (
    <div
      data-testid="sticky-drop-drawer"
      role="dialog"
      aria-label="Drop list access bar"
      className="fixed inset-x-0 bottom-0 z-[60] pointer-events-none px-3 pb-3 md:px-4 md:pb-4"
    >
      <div className="max-w-[820px] mx-auto pointer-events-auto">
        <div
          className="relative border border-[#ff1493]/70 bg-[#06000e]/95 backdrop-blur-md shadow-[0_0_28px_rgba(255,20,147,0.18)]"
        >
          {/* Hot pink top hairline */}
          <div className="absolute top-0 left-0 right-0 h-px bg-[#ff1493] opacity-70" />

          {state.status === "success" ? (
            <div
              data-testid="sticky-drop-drawer-success"
              className="px-4 py-2.5 md:py-3 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00ff41] shadow-[0_0_8px_#00ff41]" />
                <span
                  data-testid="sticky-drop-drawer-message"
                  className="font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase text-[#00ff41] truncate"
                >
                  // {state.message}
                </span>
              </div>
              <button
                onClick={dismiss}
                aria-label="Close"
                className="text-[#b4a3c7] hover:text-white transition-colors shrink-0"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="sticky-drop-drawer-form"
              className="px-3 py-2 md:px-4 md:py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            >
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <img
                  src="/brand/emblem.png"
                  alt=""
                  aria-hidden
                  className="w-5 h-5 shrink-0 drop-shadow-[0_0_8px_rgba(255,20,147,0.6)]"
                  draggable={false}
                />
                <div className="min-w-0 hidden sm:block">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493] leading-tight">
                    Drop 001 Access
                  </div>
                  <div className="font-body text-[12px] text-[#e8dff5] leading-tight mt-0.5 truncate">
                    Join before preorder unlocks.
                  </div>
                </div>
                <div className="sm:hidden font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
                  Drop 001 Access
                </div>
              </div>

              <div className="flex gap-0 w-full sm:w-auto">
                <input
                  data-testid="sticky-drop-drawer-email"
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  enterKeyHint="send"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email"
                  className="field !py-2 !px-3 sm:min-w-[210px] !text-[12px] flex-1 min-w-0 !w-auto"
                />
                <button
                  data-testid="sticky-drop-drawer-submit"
                  type="submit"
                  disabled={state.status === "loading"}
                  className="btn-primary justify-center whitespace-nowrap !py-2 !px-4 !text-[11px]"
                >
                  {state.status === "loading" ? "…" : "Authorize"}
                  <ArrowRight size={12} strokeWidth={1.5} />
                </button>
              </div>

              <button
                data-testid="sticky-drop-drawer-dismiss"
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                className="absolute top-1.5 right-2 sm:static sm:ml-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a6a90] hover:text-white transition-colors shrink-0"
              >
                <span className="hidden sm:inline">Not now</span>
                <X size={14} strokeWidth={1.5} className="sm:hidden" />
              </button>

              {state.message && state.status !== "success" && (
                <p
                  data-testid="sticky-drop-drawer-error"
                  className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#ff3b30] basis-full"
                >
                  // {state.message}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
